/**
 * Soundboard module — GJ-SB2000 rack-unit soundboard
 * Web Audio API with preloading, polyphonic playback, spectrum analyser, VU meters
 */

let audioCtx = null;
let analyser = null;
const audioBuffers = new Map();
const activeSources = new Map();
let polyphonic = true;
let lastPlayedLabel = '';
let items = [];

const padColors = [
  { color: '#e0605a', glow: 'rgba(224,96,90,0.15)',  glowInset: 'rgba(224,96,90,0.06)',  activeBg: '#1c1414' },
  { color: '#8878cc', glow: 'rgba(136,120,204,0.15)', glowInset: 'rgba(136,120,204,0.06)', activeBg: '#161420' },
  { color: '#50b870', glow: 'rgba(80,184,112,0.15)',  glowInset: 'rgba(80,184,112,0.06)',  activeBg: '#101c14' },
  { color: '#50a0d0', glow: 'rgba(80,160,208,0.15)',  glowInset: 'rgba(80,160,208,0.06)',  activeBg: '#10161e' },
  { color: '#d0a040', glow: 'rgba(208,160,64,0.15)',  glowInset: 'rgba(208,160,64,0.06)',  activeBg: '#1c1810' },
  { color: '#e07850', glow: 'rgba(224,120,80,0.15)',  glowInset: 'rgba(224,120,80,0.06)',  activeBg: '#1c1410' },
  { color: '#cc68a8', glow: 'rgba(204,104,168,0.15)', glowInset: 'rgba(204,104,168,0.06)', activeBg: '#1c1018' },
  { color: '#60c0a0', glow: 'rgba(96,192,160,0.15)',  glowInset: 'rgba(96,192,160,0.06)',  activeBg: '#101c18' },
];

function getRandomPadStyle() {
  return padColors[Math.floor(Math.random() * padColors.length)];
}

function ensureAudioContext() {
  if (audioCtx) return;
  audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  analyser = audioCtx.createAnalyser();
  analyser.fftSize = 256;
  analyser.smoothingTimeConstant = 0.75;
  analyser.connect(audioCtx.destination);
}

async function preloadAudio(soundItems) {
  ensureAudioContext();
  let loaded = 0;
  const existing = soundItems.filter(i => i._exists);
  const total = existing.length;
  const displayNow = document.getElementById('display-now');

  await Promise.all(existing.map(async (item) => {
    try {
      const resp = await fetch(item.url);
      const ab = await resp.arrayBuffer();
      const buf = await audioCtx.decodeAudioData(ab);
      audioBuffers.set(item.url, buf);
      loaded++;
      if (displayNow) {
        displayNow.textContent = 'LOADING... ' + Math.round((loaded / total) * 100) + '%';
      }
    } catch (e) {
      item._exists = false;
    }
  }));
}

function stopSound(key) {
  const e = activeSources.get(key);
  if (!e) return;
  const now = audioCtx.currentTime;
  e.gain.gain.setValueAtTime(e.gain.gain.value, now);
  e.gain.gain.linearRampToValueAtTime(0, now + 0.03);
  setTimeout(() => { try { e.source.stop(); } catch(x) { /* already stopped */ } }, 35);
  e.pad.classList.remove('pad--active');
  activeSources.delete(key);
  updateDisplay();
}

function stopAll() {
  for (const key of [...activeSources.keys()]) stopSound(key);
}

function updateDisplay() {
  const n = activeSources.size;
  const countEl = document.getElementById('display-count');
  const nowEl = document.getElementById('display-now');
  const statusEl = document.getElementById('display-status');

  if (countEl) {
    countEl.textContent = String(n).padStart(2, '0');
    countEl.classList.toggle('has-active', n > 0);
  }

  if (n === 0) {
    if (nowEl) nowEl.textContent = 'READY';
    if (statusEl) statusEl.textContent = 'IDLE';
  } else {
    if (nowEl) nowEl.textContent = lastPlayedLabel.toUpperCase();
    if (statusEl) statusEl.textContent = n + ' ACTIVE';
  }
}

function playSound(item, pad) {
  ensureAudioContext();
  if (audioCtx.state === 'suspended') audioCtx.resume();

  const key = item.key;
  if (activeSources.has(key)) { stopSound(key); return; }
  if (!polyphonic) stopAll();

  const buffer = audioBuffers.get(item.url);
  if (!buffer) return;

  const source = audioCtx.createBufferSource();
  source.buffer = buffer;
  const gainNode = audioCtx.createGain();
  source.connect(gainNode);
  gainNode.connect(analyser);
  source.start(0);

  activeSources.set(key, { source, gain: gainNode, pad });
  pad.classList.add('pad--active');
  lastPlayedLabel = item.label;
  updateDisplay();

  source.onended = () => {
    if (activeSources.has(key)) {
      pad.classList.remove('pad--active');
      activeSources.delete(key);
      updateDisplay();
    }
  };
}

// VU Meters
function initVUMeters() {
  const container = document.getElementById('vu-meters');
  if (!container) return;

  const numChannels = 8;
  const segsPerChannel = 16;
  const channels = [];

  for (let c = 0; c < numChannels; c++) {
    const col = document.createElement('div');
    col.className = 'meter-channel';
    const segs = [];
    for (let s = 0; s < segsPerChannel; s++) {
      const seg = document.createElement('div');
      seg.className = 'meter-seg';
      col.appendChild(seg);
      segs.push(seg);
    }
    const label = document.createElement('div');
    label.className = 'meter-ch-label';
    col.appendChild(label);
    container.appendChild(col);
    channels.push(segs);
  }

  const smoothed = new Float32Array(numChannels);

  function update() {
    requestAnimationFrame(update);
    if (!analyser) return;

    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);

    for (let c = 0; c < numChannels; c++) {
      const startBin = Math.floor((c / numChannels) * data.length);
      const endBin = Math.floor(((c + 1) / numChannels) * data.length);
      let sum = 0;
      for (let b = startBin; b < endBin; b++) sum += data[b];
      const raw = (sum / (endBin - startBin)) / 255;

      smoothed[c] += (raw - smoothed[c]) * 0.3;
      const val = smoothed[c];
      const litCount = Math.floor(val * segsPerChannel);

      for (let s = 0; s < segsPerChannel; s++) {
        const seg = channels[c][s];
        const pct = s / segsPerChannel;
        seg.className = 'meter-seg';
        if (s < litCount) {
          if (pct < 0.6) seg.classList.add('lit-green');
          else if (pct < 0.8) seg.classList.add('lit-yellow');
          else seg.classList.add('lit-red');
        }
      }
    }
  }
  update();
}

// Spectrum analyser
function initSpectrum() {
  const canvas = document.getElementById('spectrum');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  function resize() {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    canvas._w = rect.width;
    canvas._h = rect.height;
  }
  resize();
  window.addEventListener('resize', resize);

  const smoothed = new Float32Array(128);

  function draw() {
    requestAnimationFrame(draw);
    if (!analyser) return;

    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);

    const w = canvas._w, h = canvas._h;
    ctx.clearRect(0, 0, w, h);

    // Grid lines
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 0.5;
    for (let y = 0; y < h; y += h / 4) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    const barCount = Math.min(data.length, 64);
    const barW = w / barCount;

    for (let i = 0; i < barCount; i++) {
      const raw = data[i] / 255;
      smoothed[i] += (raw - smoothed[i]) * 0.28;
      const val = smoothed[i];
      const barH = val * h * 0.92;

      if (barH < 0.5) continue;

      const x = i * barW;
      const bw = barW - 1;

      const grad = ctx.createLinearGradient(x, h, x, h - barH);
      grad.addColorStop(0, `rgba(255,107,43,${0.3 + val * 0.5})`);
      grad.addColorStop(0.7, `rgba(255,107,43,${0.15 + val * 0.3})`);
      grad.addColorStop(1, `rgba(255,140,80,${val * 0.5})`);
      ctx.fillStyle = grad;
      ctx.fillRect(x, h - barH, bw, barH);

      if (barH > 2) {
        ctx.fillStyle = `rgba(255,160,100,${0.5 + val * 0.4})`;
        ctx.fillRect(x, h - barH, bw, 1);
      }
    }
  }
  draw();
}

export function getSoundBoardPlayer() {
  return activeSources.size > 0;
}

export function stopSoundBoard() {
  stopAll();
}

export async function initSoundboard() {
  const rackEl = document.querySelector('.soundboard-rack');
  if (!rackEl) return;

  // Read manifest from embedded JSON
  const manifestEl = document.getElementById('soundboard-manifest');
  if (!manifestEl) return;

  try {
    items = JSON.parse(manifestEl.textContent);
  } catch (e) {
    return;
  }

  // CMS assets exist by definition — preload will catch any issues
  items.forEach(item => { item._exists = true; });

  // Build pads
  const board = document.getElementById('board');
  if (!board) return;

  items.forEach((item) => {
    const style = getRandomPadStyle();

    const pad = document.createElement('div');
    pad.className = 'pad';
    if (!item._exists) pad.classList.add('missing');

    pad.style.setProperty('--pad-color', style.color);
    pad.style.setProperty('--pad-glow', style.glow);
    pad.style.setProperty('--pad-glow-inset', style.glowInset);
    pad.style.setProperty('--pad-active-bg', style.activeBg);

    const top = document.createElement('div');
    top.className = 'pad-top';

    const keyEl = document.createElement('div');
    keyEl.className = 'pad-key';
    keyEl.textContent = item.key;

    const meter = document.createElement('div');
    meter.className = 'pad-meter';

    top.appendChild(keyEl);
    top.appendChild(meter);

    const label = document.createElement('div');
    label.className = 'pad-label';
    label.textContent = item.label;

    pad.appendChild(top);
    pad.appendChild(label);

    // Mouse events
    pad.addEventListener('mousedown', (e) => {
      e.preventDefault();
      if (!item._exists) return;
      pad.classList.add('pressed');
      playSound(item, pad);
    });
    pad.addEventListener('mouseup', () => pad.classList.remove('pressed'));
    pad.addEventListener('mouseleave', () => pad.classList.remove('pressed'));

    // Touch events
    pad.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (!item._exists) return;
      pad.classList.add('pressed');
      playSound(item, pad);
    }, { passive: false });
    pad.addEventListener('touchend', (e) => {
      e.preventDefault();
      pad.classList.remove('pressed');
    }, { passive: false });
    pad.addEventListener('touchcancel', () => pad.classList.remove('pressed'));

    board.appendChild(pad);
    item._pad = pad;
  });

  // Keyboard bindings
  const shiftKeys = new Set('!@#$%^&*()_+{}|:"<>?~'.split(''));
  const keyMap = {};
  items.forEach(item => {
    const k = item.key;
    if (/^F\d+$/i.test(k)) keyMap[k.toLowerCase()] = item;
    else if (k === 'Tab') keyMap['tab'] = item;
    else if (k === 'Shift') keyMap['shift'] = item;
    else if (k === 'Enter') keyMap['enter'] = item;
    else if (k === 'Backspace') keyMap['backspace'] = item;
    else if (shiftKeys.has(k)) keyMap[k] = item;
    else if (k.length === 1 && k === k.toUpperCase() && k.match(/[A-Z]/)) keyMap[k] = item;
    else keyMap[k.toLowerCase()] = item;
  });

  document.addEventListener('keydown', (e) => {
    // Only handle when soundboard is visible
    if (!document.querySelector('.soundboard-rack')) return;
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    if (e.key === 'Escape') { stopAll(); return; }
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;

    const item = keyMap[e.key] || keyMap[e.key.toLowerCase()];
    if (item && item._exists && item._pad) {
      e.preventDefault();
      item._pad.classList.add('pressed');
      playSound(item, item._pad);
      setTimeout(() => item._pad.classList.remove('pressed'), 120);
    }
  });

  // Stop button
  const stopBtn = document.getElementById('btn-stop');
  if (stopBtn) stopBtn.addEventListener('click', stopAll);

  // Poly/mono toggle
  const modeBtn = document.getElementById('btn-mode');
  if (modeBtn) {
    modeBtn.addEventListener('click', () => {
      polyphonic = !polyphonic;
      modeBtn.innerHTML = '<span class="indicator"></span>' + (polyphonic ? 'POLY' : 'MONO');
      modeBtn.classList.toggle('mono', !polyphonic);
      const modeDisplay = document.getElementById('display-mode');
      if (modeDisplay) modeDisplay.textContent = polyphonic ? 'POLY' : 'MONO';
    });
  }

  // Preload all audio
  await preloadAudio(items);

  const displayNow = document.getElementById('display-now');
  const displayStatus = document.getElementById('display-status');
  const displayClips = document.getElementById('display-clips');
  if (displayNow) displayNow.textContent = 'READY';
  if (displayStatus) displayStatus.textContent = 'IDLE';
  if (displayClips) displayClips.textContent = audioBuffers.size;

  initSpectrum();
  initVUMeters();
}
